import { useRef, useState } from "react";
import Input from "../common/Input";
import Button from "../common/Button";
import CustomSelect from "../common/CustomSelect";
import Textarea from "../common/TextArea";
import FileUpload from "../common/FileUpload";
import { toast } from "react-hot-toast";
import { createGallery } from "../../../services/galleryServices";
import { useNavigate } from "react-router";

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "in-active", label: "Inactive" },
];

const viewOptions = [
  { value: "public", label: "Public" },
  { value: "private", label: "Private" },
];

const GalleryForm = () => {
  const navigate = useNavigate();
  const imageRef = useRef();
  const [galleryName, setGalleryName] = useState("");
  const [galleryDescription, setGalleryDescription] = useState("");
  const [status, setStatus] = useState(statusOptions[0]);
  const [viewType, setViewType] = useState(viewOptions[0]);
  const [image, setImage] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (field, value) => {
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    if (field === "galleryName") setGalleryName(value);
    if (field === "galleryDescription") setGalleryDescription(value);
    if (field === "status") setStatus(value);
    if (field === "viewType") setViewType(value);
    if (field === "image") setImage(value);
  };

  const handleSubmit = async () => {
    const errors = {};

    if (!galleryName.trim()) errors.galleryName = "Gallery Name is required";
    if (!galleryDescription.trim())
      errors.galleryDescription = "Description is required";

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fix form errors");
      return;
    }

    setFormErrors({});
    setIsLoading(true);

    try {
      const payload = {
        name: galleryName.trim(),
        description: galleryDescription.trim(),
        status: status?.value || "active",
        view: viewType?.value || "public",
        images: image,
      };

      await createGallery(payload);
      toast.success("Gallery created successfully");

      setGalleryName("");
      setGalleryDescription("");
      setStatus(statusOptions[0]);
      setViewType(viewOptions[0]);
      setImage([]);
    } catch (err) {
      toast.error(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setGalleryName("");
    setGalleryDescription("");
    setStatus(statusOptions[0]);
    setViewType(viewOptions[0]);
    setImage([]);
    setFormErrors({});
    imageRef.current?.reset();
    toast.success("Form cancelled and reset");
    navigate("/admin/gallery/list");
  };

  return (
    <div className="max-w-md p-6 bg-[#F5F0FF] rounded-lg space-y-4 font-sans">
      <Input
        label="Gallery Name"
        value={galleryName}
        onChange={(val) => handleChange("galleryName", val)}
        required
        error={formErrors.galleryName}
      />

      <Textarea
        label="Gallery Description"
        value={galleryDescription}
        onChange={(e) => handleChange("galleryDescription", e.target.value)}
        className="w-full border border-[#20202030] bg-white rounded-md px-3 py-2 text-sm focus:outline-none resize-none"
        helperText={formErrors.galleryDescription}
      />

      <CustomSelect
        label="Gallery Status"
        value={status}
        onChange={(val) => handleChange("status", val)}
        options={statusOptions}
        placeholder="Select status"
        error={formErrors.status}
      />

      <CustomSelect
        label="Gallery View"
        value={viewType}
        onChange={(val) => handleChange("viewType", val)}
        options={viewOptions}
        placeholder="Gallery view type"
        error={formErrors.viewType}
      />

      <FileUpload
        ref={imageRef}
        mode="image"
        defaultLabel="Choose Images"
        onUpload={() => {}}
        setImage={(files) => handleChange("image", files)}
        image={image}
      />

      <div className="pt-2 space-y-2">
        <Button onClick={handleSubmit} className="w-full" disabled={isLoading}>
          {isLoading ? "Processing..." : "Confirm & Add Gallery"}
        </Button>
        <Button onClick={handleCancel} variant="text" className="w-full">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default GalleryForm;
